import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Flag, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { flagApi, notifyFlagUpdated } from './flagUtils';

const TargetOption = ({ value, label, person, disabledNote }) => (
  <label className={`flex items-start gap-2 rounded-md border p-2.5 ${person ? 'cursor-pointer hover:bg-muted/50' : 'opacity-50 cursor-not-allowed'}`} data-testid={`flag-for-${value}`}>
    <RadioGroupItem value={value} disabled={!person} className="mt-0.5" />
    <span className="text-sm"><span className="font-medium">{label}</span>
      <span className="block text-xs text-muted-foreground">{person ? person.name || 'Unknown user' : disabledNote}</span></span>
  </label>
);

export const FlagForCorrectionDialog = ({ record, open, onOpenChange, onFlagged }) => {
  const [comments, setComments] = useState('');
  const [flagFor, setFlagFor] = useState('case_manager');
  const [targets, setTargets] = useState(null);
  const [saving, setSaving] = useState(false);
  const name = record ? `${record.first_name || ''} ${record.last_name || ''}`.trim() || record.participant_name : '';

  useEffect(() => {
    if (!open || !record) return;
    setFlagFor('case_manager');
    setTargets(null);
    flagApi.targets(record.id).then(setTargets).catch(() => setTargets({ case_manager: { name: '' }, success_coach: null }));
  }, [open, record]);

  const submit = async () => {
    setSaving(true);
    try {
      const flag = await flagApi.create(record.id, comments.trim(), flagFor);
      const who = flagFor === 'success_coach' ? 'success coach' : 'Case Manager';
      const email = flag.email?.status === 'sent' ? `Email sent to the ${who}.` : `Email not sent${flag.email?.reason ? `: ${flag.email.reason}` : ''}.`;
      toast.success(`Review flagged for correction. ${email}`);
      setComments('');
      onOpenChange(false);
      notifyFlagUpdated();
      onFlagged?.(flag);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not flag this review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="flag-correction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flag className="h-5 w-5 text-red-500" /> Flag for Correction</DialogTitle>
          <DialogDescription>{name}: the person you choose gets an on-screen alert, an email, a correction task and a Needs Review item on their dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Who is this flag for?</Label>
          {!targets ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <RadioGroup value={flagFor} onValueChange={setFlagFor} className="grid gap-2" data-testid="flag-for-group">
              <TargetOption value="case_manager" label="The assigned case manager" person={targets.case_manager} />
              <TargetOption value="success_coach" label="The user who completed the success coaching" person={targets.success_coach} disabledNote="No success coaching has been recorded on this review" />
            </RadioGroup>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="flag-comments">Concerns and corrective actions required</Label>
          <Textarea id="flag-comments" rows={6} value={comments} onChange={(e) => setComments(e.target.value)}
            placeholder="Describe what is wrong and what needs to be corrected..." data-testid="flag-comments-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="flag-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !comments.trim() || !targets} className="bg-red-600 hover:bg-red-700" data-testid="flag-submit-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Flag for Correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
